/**
 * Utilities for detecting Apple WebKit browsers and optimizing
 * heavy chart rendering to prevent Safari crashes.
 */

/**
 * Returns true if the browser is Apple WebKit (Safari on iOS/macOS).
 * Checks for WebKit engine while excluding Chrome/Edge/etc. which
 * also use WebKit-based engines but handle SVG better.
 */
export function isAppleWebKit(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // All iOS browsers use WebKit (even Chrome on iOS)
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  // Safari on macOS: has "Safari" but not "Chrome" or "Chromium"
  const isMacSafari = /Safari/.test(ua) && /AppleWebKit/.test(ua) && !/Chrome|Chromium|Edg|OPR|Brave/.test(ua);
  return isIOS || isMacSafari;
}

/**
 * Downsamples an array of data points to a maximum count while preserving
 * proportional representation of each pitch type.
 *
 * @param data - Array of pitch data objects
 * @param maxPoints - Maximum number of points to keep (default 500)
 * @param pitchTypeKey - Key used to group by pitch type (default "pitch_type")
 * @returns Downsampled array, or original if already small enough
 */
export function sampleData<T extends Record<string, any>>(
  data: T[],
  maxPoints: number = 500,
  pitchTypeKey: string = "pitch_type"
): T[] {
  if (!data || data.length <= maxPoints) return data;

  // Group by pitch type to preserve proportional representation
  const groups: Record<string, T[]> = {};
  for (const item of data) {
    const key = String(item[pitchTypeKey] ?? "unknown");
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }

  const ratio = maxPoints / data.length;
  const sampled: T[] = [];

  for (const key of Object.keys(groups)) {
    const group = groups[key];
    const count = Math.max(1, Math.round(group.length * ratio));
    // Evenly space the samples
    const step = group.length / count;
    for (let i = 0; i < count; i++) {
      sampled.push(group[Math.floor(i * step)]);
    }
  }

  return sampled;
}
